// ============================================
// ONGLET « ASSISTANT IA » D'UN DOSSIER
// ============================================
// Décisions de Nathan du 16/10/2026 :
//   - l'onglet ne bloque plus l'écran : les informations du dossier
//     s'affichent tout de suite, la synthèse arrive dans son cadre (squelette
//     pendant le calcul, « Réessayer » en cas d'échec) ;
//   - la « réponse suggérée » n'est plus un gabarit écrit dans le navigateur
//     (« Je prends en charge… dans les meilleurs délais ») : c'est un
//     brouillon demandé au serveur, sur tout le dossier, sans promesse ;
//   - le conseiller choisit « Réponse au client » ou « Note interne », peut
//     raccourcir, formaliser, rendre plus technique ou demander autre chose ;
//   - les actions proposées (statut, priorité, type, équipement, réponse
//     type) s'appliquent d'un clic, chacune avec sa raison, jamais toutes
//     seules ;
//   - « Lire les pièces jointes » propose des valeurs d'équipement, que le
//     conseiller applique champ par champ.
// Les cartes qui répétaient l'onglet Détails (client, équipement) sont retirées.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, Brain, Check, Copy, FileSearch, ListChecks, Loader2, MessageSquare, RefreshCw, Send, Settings, Sparkles, Wand2,
} from 'lucide-react';
import { Badge, Button, Card } from '../../shared/ui';
import { STATUS_LABELS, PRIORITY_LABELS, ISSUE_TYPE_LABELS } from '../../shared/labels';
import type { IssueType, Ticket, TicketPriority, TicketStatus, UpdateTicketPayload, User } from '../../types';
import { contexteDepuisDossier, substituerVariables } from '../tickets/reponsesTypes';
import {
  iaApi, type ActionProposee, type BrouillonConseiller, type DestinationBrouillon, type EtatAssistantIA, type LecturePieces,
  type OptionsBrouillon, type SyntheseDossier, type TonBrouillon, type ValeurLue,
} from './api';
import {
  COULEURS_ETAT_CLIENT, LIBELLES_CONFIANCE, LIBELLES_ETAT_CLIENT, decrireAction, miseAJourPourAction,
} from './libelles';

export interface AssistantIAPanelProps {
  ticket: Ticket;
  /** Applique une mise à jour du dossier (route PUT existante) ; lève l'erreur en cas d'échec. */
  surMiseAJour: (maj: UpdateTicketPayload) => Promise<void>;
  /** Place le texte dans la zone de saisie de la conversation (message ou note interne). */
  surUtiliserBrouillon: (texte: string, interne: boolean) => void;
  /** Ouvre les paramètres : fourni pour un administrateur seulement. */
  surOuvrirParametres?: () => void;
  /** Conseiller connecté, pour les variables des réponses types. */
  conseiller?: Partial<User> | null;
}

type EtatChargement<T> = { etat: 'chargement' } | { etat: 'pret'; valeur: T } | { etat: 'erreur'; message: string };

// Ce que l'IA a proposé reste affiché si l'on passe sur un autre onglet et
// qu'on revient (mémoire de la session, par dossier).
interface Memoire {
  brouillon?: BrouillonConseiller;
  texte?: string;
  destination?: DestinationBrouillon;
  lecture?: LecturePieces;
}
const memoire = new Map<string, Memoire>();

// Synthèse demandée dès l'ouverture de la fiche (prechargerAssistantIA), pour
// que l'onglet « Assistant IA » s'affiche sans attente. Le serveur la garde
// en cache tant que le dossier ne change pas : un préchargement inutilisé ne
// coûte qu'un appel par état du dossier.
const synthesesPrechargees = new Map<string, { promesse: Promise<SyntheseDossier>; le: number }>();
const DUREE_PRECHARGEMENT_MS = 60_000;

export function prechargerAssistantIA(ticketId: string): void {
  const deja = synthesesPrechargees.get(ticketId);
  if (deja && Date.now() - deja.le < DUREE_PRECHARGEMENT_MS) return;
  const promesse = iaApi.synthese(ticketId);
  promesse.catch(() => synthesesPrechargees.delete(ticketId));
  synthesesPrechargees.set(ticketId, { promesse, le: Date.now() });
}

/** Synthèse préchargée encore fraîche, consommée une seule fois. */
function prendrePrechargement(ticketId: string): Promise<SyntheseDossier> | null {
  const p = synthesesPrechargees.get(ticketId);
  synthesesPrechargees.delete(ticketId);
  return p && Date.now() - p.le < DUREE_PRECHARGEMENT_MS ? p.promesse : null;
}

/** Pour les tests. */
export function oublierAssistantIA(): void { memoire.clear(); synthesesPrechargees.clear(); }

const message = (e: unknown, defaut: string) => (e instanceof Error && e.message ? e.message : defaut);

const Squelette: React.FC<{ lignes?: number }> = ({ lignes = 3 }) => (
  <div className="space-y-2 animate-pulse" aria-hidden="true">
    {Array.from({ length: lignes }, (_, i) => (
      <div key={i} className="h-3 rounded bg-brume" style={{ width: `${92 - i * 14}%` }} />
    ))}
  </div>
);

export const AssistantIAPanel: React.FC<AssistantIAPanelProps> = ({ ticket, surMiseAJour, surUtiliserBrouillon, surOuvrirParametres, conseiller }) => {
  const ticketId = ticket.id;
  const souvenir = memoire.get(ticketId) ?? {};
  const [etatIA, setEtatIA] = useState<EtatAssistantIA | null>(null);
  const [synthese, setSynthese] = useState<EtatChargement<SyntheseDossier>>({ etat: 'chargement' });
  const [brouillon, setBrouillon] = useState<BrouillonConseiller | null>(souvenir.brouillon ?? null);
  const [texte, setTexte] = useState(souvenir.texte ?? '');
  const [destination, setDestination] = useState<DestinationBrouillon>(souvenir.destination ?? 'client');
  const [generation, setGeneration] = useState<{ enCours: boolean; erreur: string | null }>({ enCours: false, erreur: null });
  const [consigne, setConsigne] = useState('');
  const [actions, setActions] = useState<Record<number, { etat: 'enCours' | 'fait' | 'erreur'; message?: string }>>({});
  const [lecture, setLecture] = useState<LecturePieces | null>(souvenir.lecture ?? null);
  const [lectureEtat, setLectureEtat] = useState<{ enCours: boolean; erreur: string | null }>({ enCours: false, erreur: null });
  const [appliques, setAppliques] = useState<Record<string, 'enCours' | 'fait' | string>>({});
  const [copie, setCopie] = useState(false);
  const vivant = useRef(true);
  useEffect(() => { vivant.current = true; return () => { vivant.current = false; }; }, []);

  useEffect(() => {
    memoire.set(ticketId, { brouillon: brouillon ?? undefined, texte, destination, lecture: lecture ?? undefined });
  }, [ticketId, brouillon, texte, destination, lecture]);

  useEffect(() => {
    iaApi.etat().then(e => { if (vivant.current) setEtatIA(e); }).catch(() => { /* l'état reste inconnu : les boutons restent proposés */ });
  }, []);

  const chargerSynthese = useCallback((prechargee = false) => {
    setSynthese({ etat: 'chargement' });
    ((prechargee && prendrePrechargement(ticketId)) || iaApi.synthese(ticketId))
      .then(s => { if (vivant.current) setSynthese({ etat: 'pret', valeur: s }); })
      .catch(e => { if (vivant.current) setSynthese({ etat: 'erreur', message: message(e, 'La synthèse n\'a pas pu être chargée.') }); });
  }, [ticketId]);
  useEffect(() => { chargerSynthese(true); }, [chargerSynthese]);

  const iaCoupee = etatIA !== null && !etatIA.suggestions;
  // Brouillon rédigé pour une autre destination que celle choisie : il n'est
  // pas utilisable tel quel (une note interne ne part jamais au client par
  // une simple bascule). Le conseiller régénère.
  const destinationPerimee = !!brouillon && destination !== brouillon.destination;

  const demanderBrouillon = async (options: OptionsBrouillon) => {
    setGeneration({ enCours: true, erreur: null });
    try {
      const r = await iaApi.brouillon(ticketId, { destination, ...options });
      if (!vivant.current) return;
      setBrouillon(r);
      setTexte(r.brouillon);
      setDestination(r.destination);
      setActions({});
      setGeneration({ enCours: false, erreur: null });
    } catch (e) {
      if (vivant.current) setGeneration({ enCours: false, erreur: message(e, 'Le brouillon n\'a pas pu être généré.') });
    }
  };
  // Brouillon seulement au clic sur « Générer le brouillon » (demande de
  // Nathan du 22/10/2026) : lancé à chaque ouverture, il consommait des
  // jetons sur des dossiers où personne ne répondait. La synthèse, elle,
  // reste préchargée.

  const retravailler = (ton: TonBrouillon) => demanderBrouillon({ ton, brouillonActuel: texte || undefined });
  const demanderConsigne = () => {
    const c = consigne.trim();
    if (!c) return;
    demanderBrouillon({ consigne: c, brouillonActuel: texte || undefined }).then(() => { if (vivant.current) setConsigne(''); });
  };

  const appliquerAction = async (index: number, a: ActionProposee) => {
    if (a.type === 'reponse_type' && a.reponseType) {
      const contenu = substituerVariables(a.reponseType.contenu, contexteDepuisDossier(ticket, conseiller));
      setTexte(t => (t.trim() ? `${t.replace(/\s+$/, '')}\n\n${contenu}` : contenu));
      setActions(s => ({ ...s, [index]: { etat: 'fait', message: 'Ajoutée au brouillon' } }));
      return;
    }
    const maj = miseAJourPourAction(a, ticket);
    if (!maj) {
      setActions(s => ({ ...s, [index]: { etat: 'fait', message: 'Déjà en place' } }));
      return;
    }
    setActions(s => ({ ...s, [index]: { etat: 'enCours' } }));
    try {
      await surMiseAJour(maj);
      if (vivant.current) setActions(s => ({ ...s, [index]: { etat: 'fait', message: 'Appliqué' } }));
    } catch (e) {
      if (vivant.current) setActions(s => ({ ...s, [index]: { etat: 'erreur', message: message(e, 'Modification impossible') } }));
    }
  };

  const lirePieces = async () => {
    setLectureEtat({ enCours: true, erreur: null });
    try {
      const r = await iaApi.lirePiecesJointes(ticketId);
      if (!vivant.current) return;
      setLecture(r);
      setAppliques({});
      setLectureEtat({ enCours: false, erreur: null });
    } catch (e) {
      if (vivant.current) setLectureEtat({ enCours: false, erreur: message(e, 'Les pièces jointes n\'ont pas pu être lues.') });
    }
  };

  const appliquerValeur = async (v: ValeurLue) => {
    if (!v.champDossier) return;
    const actuelle = (ticket[v.champDossier] ?? '').toString().trim();
    if (actuelle && !window.confirm(`Remplacer « ${actuelle} » par « ${v.valeur} » (${v.libelle}) ?`)) return;
    setAppliques(s => ({ ...s, [v.champ]: 'enCours' }));
    try {
      await surMiseAJour({ [v.champDossier]: v.valeur } as UpdateTicketPayload);
      if (vivant.current) setAppliques(s => ({ ...s, [v.champ]: 'fait' }));
    } catch (e) {
      if (vivant.current) setAppliques(s => ({ ...s, [v.champ]: message(e, 'Modification impossible') }));
    }
  };

  const copier = async (t: string) => {
    try {
      await navigator.clipboard.writeText(t);
      setCopie(true);
      setTimeout(() => { if (vivant.current) setCopie(false); }, 1500);
    } catch { /* presse-papiers refusé : rien à faire */ }
  };

  const client = ticket.customer?.displayName || ticket.contactName || ticket.companyName || 'Client non renseigné';

  return (
    <div className="space-y-5">
      {/* Repères du dossier, tout de suite (pas d'attente du serveur). */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-graphite">
        <span className="ref text-sm text-encre">{ticket.ticketRef ?? (ticket.ticketNumber != null ? `#${ticket.ticketNumber}` : '')}</span>
        <span>{client}</span>
        <span aria-hidden="true">·</span>
        <span>{STATUS_LABELS[ticket.status as TicketStatus] ?? ticket.status}</span>
        <span aria-hidden="true">·</span>
        <span>Priorité {PRIORITY_LABELS[ticket.priority as TicketPriority]?.toLowerCase() ?? ticket.priority}</span>
        <span aria-hidden="true">·</span>
        <span>{ISSUE_TYPE_LABELS[ticket.issueType as IssueType] ?? ticket.issueType}</span>
      </div>

      {iaCoupee && (
        <div role="status" className="rounded-lg border border-trait bg-brume p-4 text-sm text-encre flex flex-wrap items-center gap-3">
          <Settings className="w-4 h-4 text-graphite" aria-hidden="true" />
          <p className="flex-1 min-w-0">
            L'assistant IA est désactivé{etatIA && !etatIA.actif ? '' : etatIA && !etatIA.cle ? ' (aucune clé API enregistrée)' : ' pour les conseillers'} : la synthèse ci-dessous est calculée sans IA, et le brouillon n'est pas disponible.
            {!surOuvrirParametres && ' Un administrateur peut l\'activer dans Paramètres.'}
          </p>
          {surOuvrirParametres && (
            <Button variant="secondary" size="sm" onClick={surOuvrirParametres}>Ouvrir les paramètres</Button>
          )}
        </div>
      )}

      {/* SYNTHÈSE */}
      <Card className="p-5 max-sm:p-4">
        <section aria-labelledby="ia-synthese-titre" aria-busy={synthese.etat === 'chargement'}>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Brain className="w-5 h-5 text-indigo-600" aria-hidden="true" />
            <h3 id="ia-synthese-titre" className="font-bold text-encre">
              {synthese.etat === 'pret' && synthese.valeur.source === 'locale' ? 'Synthèse automatique (sans IA)' : 'Synthèse IA'}
            </h3>
            {synthese.etat === 'pret' && (
              <Badge className={COULEURS_ETAT_CLIENT[synthese.valeur.etatClient]}>Client : {LIBELLES_ETAT_CLIENT[synthese.valeur.etatClient].toLowerCase()}</Badge>
            )}
            <span className="flex-1" />
            {synthese.etat !== 'chargement' && (
              <Button variant="ghost" size="xs" onClick={() => chargerSynthese()} aria-label="Actualiser la synthèse" title="Actualiser la synthèse">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>

          {synthese.etat === 'chargement' && (
            <div>
              <p className="sr-only">Synthèse en cours de calcul</p>
              <Squelette lignes={4} />
            </div>
          )}
          {synthese.etat === 'erreur' && (
            <div role="alert" className="flex flex-wrap items-center gap-3 text-sm text-red-700">
              <span className="flex-1 min-w-0">{synthese.message}</span>
              <Button variant="secondary" size="sm" onClick={() => chargerSynthese()}>Réessayer</Button>
            </div>
          )}
          {synthese.etat === 'pret' && (
            <div className="space-y-3 text-sm text-encre">
              {synthese.valeur.source === 'locale' && synthese.valeur.raisonLocale && (
                <p className="text-xs text-graphite">Calculée sans IA : {synthese.valeur.raisonLocale}.</p>
              )}
              {/* Synthèse courte (19/10/2026) : une phrase, quelques repères,
                  la prochaine action. L'avancement est déjà dit par le parcours
                  de statut en tête de fiche. */}
              <p className="font-medium leading-relaxed">{synthese.valeur.resume}</p>
              {synthese.valeur.pointsCles.length > 0 && (
                <ul className="flex flex-wrap gap-1.5" aria-label="Points clés">
                  {synthese.valeur.pointsCles.map((p, i) => (
                    <li key={i} className="px-2 py-0.5 rounded-full bg-brume text-xs text-encre">{p}</li>
                  ))}
                </ul>
              )}
              {synthese.valeur.prochainesEtapes.length > 0 && (
                <p className="text-sm"><span className="font-semibold text-sapin-800">À faire : </span>{synthese.valeur.prochainesEtapes[0]}</p>
              )}
            </div>
          )}
        </section>
      </Card>

      {/* BROUILLON PROPOSÉ */}
      <Card className="p-5 max-sm:p-4">
        <section aria-labelledby="ia-brouillon-titre" aria-busy={generation.enCours}>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <MessageSquare className="w-5 h-5 text-blue-600" aria-hidden="true" />
            <h3 id="ia-brouillon-titre" className="font-bold text-encre">Brouillon proposé</h3>
            {brouillon && <span className="text-xs text-graphite">{LIBELLES_CONFIANCE[brouillon.confiance]}</span>}
          </div>

          <fieldset className="mb-3">
            <legend className="sr-only">Destination du brouillon</legend>
            <div className="inline-flex rounded-lg border border-trait p-0.5 bg-brume" role="radiogroup" aria-label="Destination du brouillon">
              {([['client', 'Réponse au client'], ['note_interne', 'Note interne']] as const).map(([valeur, libelle]) => (
                <button
                  key={valeur}
                  type="button"
                  role="radio"
                  aria-checked={destination === valeur}
                  onClick={() => setDestination(valeur)}
                  className={`px-3 py-1.5 max-lg:py-2.5 text-xs font-semibold rounded-md transition-colors ${destination === valeur ? 'bg-white text-encre shadow-sm' : 'text-graphite hover:text-encre'}`}
                >
                  {libelle}
                </button>
              ))}
            </div>
            {brouillon && destinationPerimee && (
              <p className="text-xs text-amber-700 mt-1">
                {brouillon.destination === 'note_interne'
                  ? 'Le texte a été rédigé comme note interne : il ne peut pas être utilisé comme réponse au client. Régénérez pour le client.'
                  : 'Le texte a été rédigé comme réponse au client : régénérez pour une note interne.'}
              </p>
            )}
          </fieldset>

          {brouillon?.brouillonEcarte && (
            <div role="alert" className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
              <span>Brouillon écarté par le contrôle de sécurité ({brouillon.brouillonEcarte}). Régénérez ou rédigez vous-même.</span>
            </div>
          )}

          {generation.enCours && !texte ? (
            <div className="rounded-lg border border-trait p-4 mb-3">
              <p className="text-xs text-graphite mb-3 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />L'IA rédige à partir de tout le dossier…</p>
              <Squelette lignes={5} />
            </div>
          ) : (brouillon || texte) ? (
            <textarea
              aria-label="Texte du brouillon"
              value={texte}
              onChange={e => setTexte(e.target.value)}
              rows={Math.min(14, Math.max(6, texte.split('\n').length + 1))}
              className={`w-full mb-3 rounded-lg border p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 ${destination === 'note_interne' ? 'border-dashed border-slate-400 bg-brume' : 'border-trait bg-white'} ${generation.enCours ? 'opacity-60' : ''}`}
            />
          ) : (
            <p className="text-sm text-graphite mb-3">
              {iaCoupee ? 'Brouillon indisponible : l\'IA est désactivée.' : 'L\'IA lit tout le dossier (conversation, notes internes, autres dossiers de la machine et du client, réponses types) et propose un texte que vous relisez avant envoi.'}
            </p>
          )}

          {generation.erreur && (
            <div role="alert" className="mb-3 flex flex-wrap items-center gap-3 text-sm text-red-700">
              <span className="flex-1 min-w-0">{generation.erreur}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-3">
            <Button variant={brouillon ? 'secondary' : 'primary'} size="sm" disabled={generation.enCours || iaCoupee} onClick={() => demanderBrouillon({})}>
              {generation.enCours ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              {generation.erreur ? 'Réessayer' : brouillon ? 'Régénérer' : 'Générer le brouillon'}
            </Button>
            {brouillon && (
              <>
                <Button variant="ghost" size="sm" className="border border-trait" disabled={generation.enCours || iaCoupee} onClick={() => retravailler('plus_court')}>Plus court</Button>
                <Button variant="ghost" size="sm" className="border border-trait" disabled={generation.enCours || iaCoupee} onClick={() => retravailler('plus_formel')}>Plus formel</Button>
                <Button variant="ghost" size="sm" className="border border-trait" disabled={generation.enCours || iaCoupee} onClick={() => retravailler('plus_technique')}>Plus technique</Button>
              </>
            )}
          </div>

          <form className="flex gap-2 mb-4" onSubmit={e => { e.preventDefault(); demanderConsigne(); }}>
            <label htmlFor="ia-consigne" className="sr-only">Demander quelque chose à l'IA</label>
            <input
              id="ia-consigne"
              type="text"
              value={consigne}
              maxLength={1000}
              onChange={e => setConsigne(e.target.value)}
              placeholder="Demander quelque chose à l'IA (ex. demander la date d'installation)"
              disabled={iaCoupee}
              className="flex-1 min-w-0 px-3 py-2 text-sm border border-trait rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-brume"
            />
            <Button type="submit" variant="secondary" size="sm" disabled={!consigne.trim() || generation.enCours || iaCoupee} aria-label="Envoyer la demande à l'IA">
              <Wand2 className="w-4 h-4" />
            </Button>
          </form>

          {texte.trim() && (
            <div className="flex gap-2">
              <Button
                variant="primary"
                className="flex-1"
                disabled={generation.enCours || destinationPerimee}
                onClick={() => { if (!destinationPerimee) surUtiliserBrouillon(texte, destination === 'note_interne'); }}
              >
                <Send className="w-4 h-4 mr-2" />
                {destinationPerimee
                  ? (destination === 'client' ? 'Régénérez pour le client' : 'Régénérez pour une note interne')
                  : destination === 'note_interne' ? 'Utiliser comme note interne' : 'Utiliser comme réponse au client'}
              </Button>
              <Button variant="secondary" onClick={() => copier(texte)} aria-label="Copier le brouillon" title="Copier le brouillon">
                {copie ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          )}
          <p className="sr-only" aria-live="polite">{copie ? 'Brouillon copié' : ''}</p>
        </section>
      </Card>

      {/* IL MANQUE */}
      {brouillon && brouillon.informationsManquantes.length > 0 && (
        <Card className="p-5 max-sm:p-4">
          <h3 className="font-bold text-encre mb-2 flex items-center gap-2"><ListChecks className="w-5 h-5 text-amber-600" aria-hidden="true" />Il manque</h3>
          <ul className="space-y-1 text-sm text-encre">
            {brouillon.informationsManquantes.map((m, i) => (
              <li key={i} className="flex gap-2"><span className="text-amber-600" aria-hidden="true">•</span><span>{m}</span></li>
            ))}
          </ul>
        </Card>
      )}

      {/* ACTIONS PROPOSÉES */}
      {brouillon && brouillon.actionsProposees.length > 0 && (
        <Card className="p-5 max-sm:p-4">
          <h3 className="font-bold text-encre mb-1">Actions proposées</h3>
          <p className="text-xs text-graphite mb-3">Rien n'est appliqué sans votre clic.</p>
          <ul className="space-y-2">
            {brouillon.actionsProposees.map((a, i) => {
              const etat = actions[i];
              return (
                <li key={i} className="flex flex-wrap sm:flex-nowrap items-start gap-3 rounded-lg border border-trait p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-encre">{decrireAction(a)}</p>
                    {a.raison && <p className="text-xs text-graphite mt-0.5">{a.raison}</p>}
                    {etat?.etat === 'erreur' && <p role="alert" className="text-xs text-red-700 mt-1">{etat.message}</p>}
                  </div>
                  {etat?.etat === 'fait' ? (
                    <span className="text-xs font-semibold text-sapin-700 flex items-center gap-1"><Check className="w-3.5 h-3.5" aria-hidden="true" />{etat.message}</span>
                  ) : (
                    <Button variant="secondary" size="sm" disabled={etat?.etat === 'enCours'} onClick={() => appliquerAction(i, a)} aria-label={`${a.type === 'reponse_type' ? 'Insérer' : 'Appliquer'} : ${decrireAction(a)}`}>
                      {etat?.etat === 'enCours' ? <Loader2 className="w-4 h-4 animate-spin" /> : a.type === 'reponse_type' ? 'Insérer' : 'Appliquer'}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {/* PIÈCES JOINTES */}
      <Card className="p-5 max-sm:p-4">
        <section aria-labelledby="ia-pieces-titre" aria-busy={lectureEtat.enCours}>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <FileSearch className="w-5 h-5 text-graphite" aria-hidden="true" />
            <h3 id="ia-pieces-titre" className="font-bold text-encre flex-1">Pièces jointes</h3>
            {etatIA?.extraction !== false && (
              <Button variant="secondary" size="sm" disabled={lectureEtat.enCours} onClick={lirePieces}>
                {lectureEtat.enCours ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {lecture ? 'Relire les pièces jointes' : 'Lire les pièces jointes'}
              </Button>
            )}
          </div>
          {etatIA?.extraction === false ? (
            <p className="text-sm text-graphite">Lecture des pièces jointes désactivée (Paramètres, Intelligence artificielle).</p>
          ) : !lecture && !lectureEtat.enCours ? (
            <p className="text-sm text-graphite">Les 4 photos ou PDF les plus récents sont lus pour proposer marque, modèle, numéro de série, code erreur et date d'achat. Rien n'est enregistré sans votre clic.</p>
          ) : null}
          {lectureEtat.enCours && <Squelette lignes={3} />}
          {lectureEtat.erreur && (
            <div role="alert" className="flex flex-wrap items-center gap-3 text-sm text-red-700 mt-2">
              <span className="flex-1 min-w-0">{lectureEtat.erreur}</span>
              <Button variant="secondary" size="sm" onClick={lirePieces}>Réessayer</Button>
            </div>
          )}
          {lecture && !lectureEtat.enCours && (
            <div className="space-y-3 mt-2">
              {lecture.valeurs.length === 0 ? (
                <p className="text-sm text-graphite">Aucune valeur lisible n'a été trouvée.</p>
              ) : (
                <ul className="divide-y divide-trait">
                  {lecture.valeurs.map(v => {
                    const actuelle = v.champDossier ? (ticket[v.champDossier] ?? '').toString().trim() : '';
                    const etat = appliques[v.champ];
                    return (
                      <li key={v.champ} className="py-2 flex flex-wrap sm:flex-nowrap items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-graphite">{v.libelle}{v.piece ? ` · ${v.piece.nom}` : ''} · {LIBELLES_CONFIANCE[v.confiance]}</p>
                          <p className="text-sm font-semibold text-encre tnum break-all">{v.valeur}</p>
                          {actuelle && actuelle !== v.valeur && <p className="text-xs text-graphite">Dans le dossier : {actuelle}</p>}
                          {etat && etat !== 'enCours' && etat !== 'fait' && <p role="alert" className="text-xs text-red-700">{etat}</p>}
                        </div>
                        {!v.champDossier ? (
                          <Button variant="ghost" size="sm" className="border border-trait" onClick={() => copier(v.valeur)} aria-label={`Copier : ${v.libelle}`}>
                            <Copy className="w-4 h-4" />
                          </Button>
                        ) : etat === 'fait' || actuelle === v.valeur ? (
                          <span className="text-xs font-semibold text-sapin-700 flex items-center gap-1"><Check className="w-3.5 h-3.5" aria-hidden="true" />{etat === 'fait' ? 'Appliqué' : 'Déjà renseigné'}</span>
                        ) : (
                          <Button variant="secondary" size="sm" disabled={etat === 'enCours'} onClick={() => appliquerValeur(v)} aria-label={`${actuelle ? 'Remplacer' : 'Appliquer'} : ${v.libelle}`}>
                            {etat === 'enCours' ? <Loader2 className="w-4 h-4 animate-spin" /> : actuelle ? 'Remplacer' : 'Appliquer'}
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {lecture.remarque && <p className="text-xs text-graphite">{lecture.remarque}</p>}
              {lecture.ignorees.length > 0 && (
                <details className="text-xs text-graphite">
                  <summary className="cursor-pointer">{lecture.ignorees.length} pièce{lecture.ignorees.length > 1 ? 's' : ''} non lue{lecture.ignorees.length > 1 ? 's' : ''}</summary>
                  <ul className="mt-1 space-y-0.5">
                    {lecture.ignorees.map(p => <li key={p.id}>{p.nom} : {p.raison}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </section>
      </Card>
    </div>
  );
};

export default AssistantIAPanel;
